import { useState, useEffect, useCallback } from "react";
import { AlertTriangle, PauseCircle, RefreshCw, Ship } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

// Format an ISO timestamp as "N minutes/hours/days ago". Returns "just now"
// for anything < 60s. Used for the "last successful scrape" indicator.
const timeAgo = (iso) => {
  if (!iso) return "never";
  try {
    const then = new Date(iso).getTime();
    const secs = Math.max(1, Math.floor((Date.now() - then) / 1000));
    if (secs < 60) return "just now";
    if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)} h ago`;
    return `${Math.floor(secs / 86400)} d ago`;
  } catch {
    return "unknown";
  }
};

// Human names + cadence terminology per scraper. Terminology matters:
// news + weather run every 15 min, so "126 runs in a row" makes sense
// but "126 nights in a row" is misleading (would imply ~4 months, not
// the actual ~31 hours). Cruise / lottery / fuel / pharmacy are truly
// nightly.
const LABELS = {
  news: { name: "News (all sources + obituaries)", icon: AlertTriangle,
          unit: "runs", cadenceHint: "runs every 15 min" },
  weather: { name: "Weather (Open-Meteo)", icon: AlertTriangle,
             unit: "runs", cadenceHint: "runs every 15 min" },
  cruise_cruisemapper: {
    name: "Cruise Schedule (CruiseMapper)",
    icon: Ship,
    runNowUrl: "/admin/scrape/cruise-run-now",
    unit: "nights", cadenceHint: "runs nightly",
  },
  lottery: { name: "Lottery Results", icon: AlertTriangle,
             unit: "runs", cadenceHint: "runs hourly" },
  fuel_prices: { name: "Fuel Prices", icon: AlertTriangle,
                 unit: "runs", cadenceHint: "runs daily" },
  pharmacy_botica: { name: "Pharmacy On Duty (Aruba)", icon: AlertTriangle,
                     unit: "runs", cadenceHint: "runs daily" },
};

export const ScraperHealthBanner = () => {
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/scrape-health");
      setHealth(r.data);
    } catch { /* silent — banner is best-effort */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async (key) => {
    const meta = LABELS[key];
    if (!meta) return;
    setBusy((s) => ({ ...s, [key]: true }));
    try {
      const r = await api.post(meta.runNowUrl);
      const status = r.data.last_status;
      const rows = r.data.total_rows_written ?? 0;
      if (status === "ok") {
        toast.success(`${meta.name}: ${rows} rows scraped`);
      } else {
        toast.error(
          `${meta.name}: ${status} — failed islands: ${(r.data.islands_failed || []).join(", ") || "n/a"}`
        );
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Run now failed");
    } finally {
      setBusy((s) => ({ ...s, [key]: false }));
    }
  };

  // Reset the `consecutive_failures` counter for a scraper. Used after
  // the underlying failure cause is fixed — otherwise the banner keeps
  // showing a stale scary count that no longer reflects reality (e.g.
  // "126 runs in a row" when the last 5 runs actually succeeded).
  const resetCounter = async (key) => {
    if (!window.confirm(
      `Reset consecutive_failures for ${key}?\n\n` +
      `This clears the counter but does NOT change last_success_at or ` +
      `run a new scrape. Use this after fixing the underlying issue.`
    )) return;
    const busyKey = `reset-${key}`;
    setBusy((s) => ({ ...s, [busyKey]: true }));
    try {
      await api.post(`/admin/scrape-health/${key}/reset`);
      toast.success(`Counter reset for ${key}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally {
      setBusy((s) => ({ ...s, [busyKey]: false }));
    }
  };

  if (!health) return null;
  const scrapers = health.scrapers || [];
  // Split the alerting set two ways:
  //  • `alerting`   — real red banners; scrapers with `alert=true` and NOT paused
  //  • `pausedNotes` — small calm info lines for paused scrapers with an
  //                    underlying failure streak. Suppresses the red card + the
  //                    "Reset counter" CTA (we've deliberately decided NOT to
  //                    act on these) but keeps a visible signal so the failure
  //                    streak isn't invisible either.
  //  • `outageNotes` — same calm-info treatment for scrapers whose upstream
  //                    is confirmed unreachable (network-level outside our
  //                    system). Backend suppresses `alert=true` for these
  //                    while the outage is active so we render a calm note
  //                    here instead of a red panic card.
  const alerting = scrapers.filter(
    (s) => s.alert && !s.paused && !s.outage_active
  );
  const pausedNotes = scrapers.filter(
    (s) => s.paused && (s.consecutive_failures || 0) > 0
  );
  const outageNotes = scrapers.filter(
    (s) => s.outage_active && !s.paused
  );
  const killSwitch = health.kill_switch_active;
  // Nothing to render if everything is healthy and no kill-switch is on
  if (
    alerting.length === 0 &&
    pausedNotes.length === 0 &&
    outageNotes.length === 0 &&
    !killSwitch
  ) return null;

  return (
    <div
      className="max-w-7xl mx-auto px-6 pt-4"
      data-testid="scraper-health-banner"
    >
      {killSwitch && (
        <div
          className="mb-3 rounded-lg border-l-4 border-l-[#F59E0B] bg-[#FEF3C7]/70 p-3 flex items-start gap-3"
          data-testid="scraper-killswitch-banner"
        >
          <AlertTriangle size={18} className="text-[#92400E] mt-0.5" />
          <div className="text-sm text-[#92400E]">
            <strong>Scraper kill switch is active</strong> — DISABLE_SCRAPERS=1
            in backend env. All nightly scrapers are paused. Unset the env
            var and restart the backend to resume.
          </div>
        </div>
      )}
      {outageNotes.map((s) => {
        const meta = LABELS[s.scraper] || { name: s.scraper };
        return (
          <div
            key={`outage-${s.scraper}`}
            className="mb-3 rounded-lg border-l-4 border-l-[#F59E0B] bg-[#F9FAFB] p-3 flex items-start gap-3"
            data-testid={`scraper-outage-note-${s.scraper}`}
          >
            <AlertTriangle size={16} className="text-[#F59E0B] mt-0.5" />
            <div className="flex-1 text-sm text-[#6B7280] leading-snug">
              <div>
                <span className="text-[10px] uppercase tracking-widest font-semibold text-[#92400E] mr-2">
                  SOURCE OFFLINE
                </span>
                <strong className="text-[#374151]">{meta.name || s.scraper}</strong>
                {" — external hosting outage on the source's end. Retry "
                  + "logic is functioning correctly (verified in logs) and "
                  + "will auto-recover the moment connectivity is restored. "
                  + "No manual action needed."}
              </div>
              {s.outage_reason && (
                <div className="text-xs mt-1 text-[#92400E]">
                  {s.outage_reason}
                </div>
              )}
            </div>
          </div>
        );
      })}
      {pausedNotes.map((s) => {
        const meta = LABELS[s.scraper] || { name: s.scraper };
        return (
          <div
            key={`paused-${s.scraper}`}
            className="mb-3 rounded-lg border-l-4 border-l-[#F59E0B] border-l-dashed bg-[#F9FAFB] p-3 flex items-start gap-3"
            data-testid={`scraper-paused-note-${s.scraper}`}
          >
            <PauseCircle size={16} className="text-[#F59E0B] mt-0.5" />
            <div className="flex-1 text-sm text-[#6B7280] leading-snug">
              <div>
                <span className="text-[10px] uppercase tracking-widest font-semibold text-[#92400E] mr-2">
                  PAUSED
                </span>
                <strong className="text-[#374151]">{meta.name || s.scraper}</strong>
                {" has an underlying failure streak of "}
                <strong>{s.consecutive_failures}</strong>
                {" — intentional, no action needed."}
              </div>
              {s.pause_reason && (
                <div className="text-xs mt-1 text-[#92400E]">
                  See backlog: {s.pause_reason}
                </div>
              )}
            </div>
          </div>
        );
      })}
      {alerting.map((s) => {
        const meta = LABELS[s.scraper] || { name: s.scraper, icon: AlertTriangle,
                                             unit: "runs" };
        const Icon = meta.icon;
        return (
          <div
            key={s.scraper}
            className="mb-3 rounded-lg border-l-4 border-l-[#E63946] bg-[#FEE2E2]/60 p-3 flex items-start gap-3"
            data-testid={`scraper-alert-${s.scraper}`}
          >
            <Icon size={18} className="text-[#7F1D1D] mt-0.5" />
            <div className="flex-1 text-sm text-[#7F1D1D] space-y-1">
              <div>
                <strong>{meta.name}</strong> has failed{" "}
                <strong>{s.consecutive_failures}</strong> {meta.unit || "runs"} in a row
                (threshold: {s.alert_threshold}).
                {meta.cadenceHint && (
                  <span className="text-xs opacity-70"> · {meta.cadenceHint}</span>
                )}
              </div>
              <div className="text-xs text-[#7F1D1D]/85">
                Last successful run: <strong>{timeAgo(s.last_success_at)}</strong>
                {" · "}
                Last attempt: {timeAgo(s.last_run_at)}
                {s.islands_failed?.length > 0 && (
                  <> · Failed islands: {s.islands_failed.join(", ")}</>
                )}
              </div>
              {s.last_error && (
                <div
                  className="text-xs mt-1 px-2 py-1 rounded font-mono break-all bg-white/60"
                  style={{ color: "#7F1D1D" }}
                  data-testid={`scraper-alert-error-${s.scraper}`}
                >
                  <strong>Error:</strong> {s.last_error}
                </div>
              )}
              <div className="text-xs text-[#7F1D1D]/70">
                Widgets are showing cached data. The site itself is fine —
                readers see "Last updated N days ago" in the affected widget.
              </div>
            </div>
            <div className="flex flex-col gap-1 items-end">
              {meta.runNowUrl && (
                <button
                  onClick={() => runNow(s.scraper)}
                  disabled={busy[s.scraper]}
                  className="text-xs px-2 py-1 rounded bg-white text-[#7F1D1D] border border-[#7F1D1D]/30 hover:bg-[#FEE2E2] disabled:opacity-50"
                  data-testid={`scraper-alert-run-now-${s.scraper}`}
                >
                  <RefreshCw size={12} className="inline mr-1" />
                  {busy[s.scraper] ? "Running…" : "Run now"}
                </button>
              )}
              <button
                onClick={() => resetCounter(s.scraper)}
                disabled={busy[`reset-${s.scraper}`]}
                className="text-xs px-2 py-1 rounded bg-white text-[#7F1D1D] border border-[#7F1D1D]/30 hover:bg-[#FEE2E2] disabled:opacity-50"
                data-testid={`scraper-alert-reset-${s.scraper}`}
                title="Clears consecutive_failures. Use after fixing the underlying issue so the banner stops showing a stale count."
              >
                Reset counter
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
